import { startCase } from "lodash";
import { SampleAvailabilityMessages } from "../../enums";
import { TimeEnum } from "../../lib/enums";
import {
  classificationByLabels,
  getClassificationByLabels
} from "../../lib/enums/ThreatStatusEnum";
import { consts, route } from "../../shim/jsbridge";
import { FILTER_KEYWORDS } from "../search/consts";
import { SAMPLE_ORIGINS } from "../search/components/filters/consts";
import { ANALYZING_CONSTS } from "./consts";
import { EXCLUDE_TRANSFORM_MAPPING, FIELD_NAME_MAPPINGS } from "./mappings";

const reCamelCase = /(\W)?([a-z0-9]+)(([A-Z])([a-z0-9]+))+/;
const reCamelCaps = /([a-z0-9_]+)([A-Z])/;

const makePbStringTitle = str => {
  // Convert strings of style "invalid_certificate_k" to "Invalid Certificate"
  const trimmed = str.replace(/_(k|t)$/, "");
  const words = trimmed.replace("_", " ");
  return startCase(words.toLowerCase());
};

const transform = str => {
  if (EXCLUDE_TRANSFORM_MAPPING.includes(str)) {
    return str;
  } else if (Object.keys(FIELD_NAME_MAPPINGS).includes(str)) {
    return FIELD_NAME_MAPPINGS[str];
  }

  try {
    let value;
    if (str.match(reCamelCase)) {
      value = str.sub(reCamelCaps, /'\1 \2'/);
    } else {
      value = str;
    }
    return makePbStringTitle(value);
  } catch (e) {
    console.log(str);
    return str;
  }
};

export const convertForDisplay = str => {
  try {
    return transform(str);
  } catch (e) {
    console.log(e);
    return str;
  }
};

export const formatTypeLabel = (type, subtype, value) =>
  `${type}${subtype && subtype !== "None" ? `/${subtype}` : ""}${
    value ? ": " : ""
  }`;

export const generateSeenData = (
  fSeenLocalData,
  lSeenLocalData,
  fSeenCloudData,
  lSeenCloudData
) => {
  let firstSeenLocation;
  let firstSeenDate;
  let lastSeenLocation;
  let lastSeenDate;

  if (fSeenCloudData) {
    firstSeenLocation = fSeenLocalData < fSeenCloudData ? "local" : "cloud";
    firstSeenDate =
      fSeenLocalData < fSeenCloudData ? fSeenLocalData : fSeenCloudData;
  } else {
    firstSeenLocation = "local";
    firstSeenDate = fSeenLocalData;
  }

  if (lSeenCloudData) {
    lastSeenLocation = lSeenLocalData > lSeenCloudData ? "local" : "cloud";
    lastSeenDate =
      lSeenLocalData > lSeenCloudData ? lSeenLocalData : lSeenCloudData;
  } else {
    lastSeenLocation = "local";
    lastSeenDate = lSeenLocalData;
  }

  return [firstSeenLocation, firstSeenDate, lastSeenLocation, lastSeenDate];
};

export const goodwareLabels = {
  0: "Whitelisted / Reputable Origin",
  1: "Whitelisted / Reputable Origin",
  2: "Likely Benign / Public Origin",
  3: "Likely Benign / Public Origin",
  4: "Not a Known Threat / Unverified Origin",
  5: "Not a Known Threat / Origin Unknown"
};

export const htmlEncode = string =>
  string
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/'/g, "&#39;")
    .replace(/"/g, "&#34;");

export const dec2hex = (data, type) => {
  // type 64 or 32 bit
  let hex = Number(data).toString(16);
  if (!type) {
    hex = "00000000".substr(0, 8 - hex.length) + hex;
  } else {
    hex = "0000000000000000".substr(0, 16 - hex.length) + hex;
  }
  return hex;
};

export const createSelectedRowKey = (index, page, row) => {
  if (!row || typeof row !== "object") {
    return `row[${index}]_page[${page}]`;
  }

  const rowKeys = ["uuid", "sha1", "hash", "id"];
  for (const key of rowKeys) {
    if (key in row && row[key] != null) {
      return row[key];
    }
  }

  return `row[${index}]_page[${page}]`;
};

export const arrayToAnObject = (data, page) =>
  data.reduce(
    (object, item, index) => ({
      ...object,
      ...{ [createSelectedRowKey(index, page)]: item }
    }),
    {}
  );

export const mapClientSidePagedElements = (tableData = [], pageSize) =>
  tableData.reduce((obj, item, index) => {
    const calculatedItemRow = index % pageSize;
    const calculatedItemPage = Math.floor(index / pageSize) + 1;
    return {
      ...obj,
      ...{
        [createSelectedRowKey(calculatedItemRow, calculatedItemPage)]: item
      }
    };
  }, {});

export const isEmpty = object => Object.keys(object).length === 0;

export const objectValues = object => Object.values(object);

export const objectContainsEvery = (object1, object2) =>
  Object.keys(object1).every(key => !!object2[key]);

export const getSampleDetailsSectionUrl = (
  section,
  params = {},
  sha1,
  sourceId,
  relationshipId
) => {
  const navRoute = section ? "samples_details_section" : "samples_details_main";
  const navPath = section
    ? { source_id: sourceId, hash_value: sha1, section }
    : { source_id: sourceId, hash_value: sha1 };
  const queryParams = relationshipId
    ? { r: relationshipId, ...params }
    : { ...params };
  return route(
    navRoute,
    navPath,
    Object.keys(queryParams).length ? queryParams : null
  );
};

export const threatTitleWithPlaceholder = (
  threatName,
  classification,
  factor
) => {
  if (threatName) {
    return threatName.split(".")[1];
  }
  if (
    getClassificationByLabels(classification) ===
    classificationByLabels.GOODWARE
  ) {
    if (factor <= 1) {
      return "Goodware";
    }
    if (factor <= 3) {
      return "Likely benign";
    }
    if (factor <= 5) {
      return "No known threats";
    }
  }
  return "No Classification";
};

export const formatDateUTC = dateString => {
  if (!dateString) {
    return null;
  }

  const addZero = function addZ(n) {
    return n < 10 ? `0${n}` : `${n}`;
  };

  const date = new Date(dateString);
  const year = date.getUTCFullYear();
  const month = addZero(date.getUTCMonth() + 1);
  const day = addZero(date.getUTCDate());
  const hours = addZero(date.getUTCHours());
  const minutes = addZero(date.getUTCMinutes());

  return `${year}-${month}-${day} ${hours}:${minutes} UTC`;
};

export const generateEndpoint = (hash, sourceId, page, section, params = "") =>
  `/${hash}/source/${sourceId}/${page}/${section}/${params}`;

export const getSearchPivotLink = (
  query,
  selections = {},
  openTiCard = false,
  location = "local"
) =>
  route("search", [], {
    q: query,
    location,
    selections: new URLSearchParams({
      time_type: FILTER_KEYWORDS.FIRST_SEEN,
      period_type: TimeEnum.ALL,
      sample_origin: Object.values(SAMPLE_ORIGINS).join(),
      ...selections
    }),
    ...(openTiCard
      ? {
          openticard: true
        }
      : {})
  });

export const sampleNotAvailableForFetch = (downloadable, fileSize) => {
  const isSampleAvailable =
    downloadable &&
    fileSize !== undefined &&
    fileSize <= consts.ticloud_max_fetch_file_size;

  const fetchUnavailableMessage =
    fileSize >= consts.ticloud_max_fetch_file_size
      ? SampleAvailabilityMessages.MAXIMUM
      : SampleAvailabilityMessages.CLOUD_UNAVAILABLE;

  return [isSampleAvailable, fetchUnavailableMessage];
};

export const getRouteForSampleSummaryRedirect = section => {
  if (section != null) {
    return "api_get_sample_section_url";
  } else {
    return "api_get_sample_url";
  }
};
export const pathToSS = (hash, sourceId, section, relationshipId) =>
  `/${hash}/source/${sourceId}/${section}${
    relationshipId ? `?r=${relationshipId}` : ""
  }`;

export const pathToRLCS = (hash, sourceId, section, relationshipId) =>
  `${pathToSS(hash, sourceId, "rl_dynamic_analysis")}/${section}${
    relationshipId ? `?r=${relationshipId}` : ""
  }`;

export const pathToExtractedFile = (hash, sourceId, path, relationshipId) =>
  `${pathToSS(hash, sourceId, "extracted-files/")}?path=${encodeURIComponent(
    path
  )}&r=${relationshipId}`;

const RLCS_ANALYSIS_CONSTS = [
  "File is queued for analysis",
  "Analyzing the sample",
  "Generating the report"
];

export const isSampleAnalyzing = status =>
  ANALYZING_CONSTS.includes(status ?? "");

export const isSampleAnalyzingOnRlcs = status =>
  isSampleAnalyzing(status) || RLCS_ANALYSIS_CONSTS.includes(status);

export const getSelectedLayout = (selected, layouts) => {
  if (!layouts) return undefined;

  const typeLayouts = layouts[selected?.type] ?? [];
  const layout = typeLayouts?.find(item => item.id === selected.id);

  if (layout) return { ...selected, name: layout.name };

  const { id, name } = layouts.rl.at(0) ?? {};
  return { id, name, type: "rl" };
};
